import { useMemo } from "react";
import { generatePath, Navigate, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { CALENDAR_TRIAL_DAY_ROUTE, CALENDAR_TRIAL_LESSON_ROUTE, CALENDAR_TRIAL_ROUTE } from "../../../app/router/routes";
import { IGetCalendarFilterOptionsResponse } from "../../../entities/calendar";
import { ITrialCalendarFilter } from "../../../entities/trialLesson";
import { ChangeDayInCalendar } from "../../../features/changeDayInCalendar";
import { scrollLayoutToTop } from "../../../shared/lib/helpers/scrollLayoutToTop";
import { TrialCalendarDay } from "../../../widgets/trialCalendarDay";
import {
	getTrialCreationDraftFromSearchParams,
	isTrialCalendarCreationFilterComplete,
	trialCreationDraftToFilter,
} from "../../../features/trialLessonCreate";
import classes from './calendarTrialDay.module.scss'

interface ICalendarOutletContext {
    trialFilter: ITrialCalendarFilter;
    trialFilterOptions: IGetCalendarFilterOptionsResponse | null;
    setIsDayContentOpen: (isOpen: boolean) => void;
}

const getDateFromParam = (dateParam?: string): Date | null => {
    const match = dateParam?.match(/^(\d{4})-(\d{2})-(\d{2})$/)

    if(!match) return null

    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])
    const date = new Date(year, month - 1, day)

    if(
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ){
        return null
    }

    return date
}

const getDatePath = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `/calendar/trial/day/${year}-${month}-${day}`
}

export default function CalendarTrialDayPage() {

    const {date: dateParam, lessonId} = useParams<{date: string; lessonId?: string}>()
    const {trialFilter, trialFilterOptions, setIsDayContentOpen} = useOutletContext<ICalendarOutletContext>()
	const navigate = useNavigate()
	const location = useLocation()
	const date = useMemo(() => getDateFromParam(dateParam), [dateParam])
	const creationMode = new URLSearchParams(location.search).get('createTrial') === '1'
	const creationFilter = useMemo(() => {
		return creationMode
			? trialCreationDraftToFilter(
				getTrialCreationDraftFromSearchParams(new URLSearchParams(location.search)),
			)
			: isTrialCalendarCreationFilterComplete(trialFilter)
				? trialFilter
				: undefined
	}, [creationMode, location.search, trialFilter])

    if(!date){
        return <Navigate to={{pathname: CALENDAR_TRIAL_ROUTE.path, search: location.search}} replace />
    }

    return (
        <section className={classes.container}>
            <TrialCalendarDay
                date={date}
				filter={trialFilter}
				creationFilter={creationFilter}
                filterOptions={trialFilterOptions}
				creationMode={Boolean(creationFilter)}
                availableSlotsOnly={creationMode}
                trialLessonId={lessonId}
                onContentOpen={scrollLayoutToTop}
                onContentChange={setIsDayContentOpen}
                onSelectTrialLesson={selectedLessonId => {
                    navigate({
                        pathname: generatePath(CALENDAR_TRIAL_LESSON_ROUTE.path, {
                            date: dateParam,
                            lessonId: selectedLessonId,
                        }),
                        search: location.search,
                    })
                }}
                onCloseTrialLesson={() => {
                    navigate({
                        pathname: generatePath(CALENDAR_TRIAL_DAY_ROUTE.path, {date: dateParam}),
                        search: location.search,
                    }, {replace: true})
                }}
            />
            <section className={classes.calendarSticky}>
                <ChangeDayInCalendar
                    date={date}
                    trialFilter={trialFilter}
                    trialCreationFilter={creationFilter}
                    onSelectDate={nextDate => {
                        navigate({pathname: getDatePath(nextDate), search: location.search})
                    }}
                />
            </section>
        </section>
    )
}
